const FOCUS_CARD_SELECTORS = [
  '[itemtype*="schema.org/Product"]',
  '[itemtype*="schema.org/Offer"]',
  '.product-card',
  '.product-item',
  '.product-grid-item',
  '.woocommerce-loop-product__link',
  '.product-grid__item',
  'article[class*="product"]',
  'li[class*="product"]',
  'div[class*="product-card"]',
  'div[class*="product-item"]',
].join(', ');

const MIN_CARD_HEIGHT_PX = 100;
const MIN_CARD_WIDTH_PX = 80;
const MAX_OBSERVED_NODES = 24;
const MIN_REPEATED_SIBLINGS = 2;

export type FocusCardCandidate = {
  element: Element;
  productName?: string;
  productUrl?: string;
};

function textOf(el: Element | null | undefined): string | undefined {
  const text = el?.textContent?.replace(/\s+/g, ' ').trim();
  return text || undefined;
}

function resolveProductName(el: Element): string | undefined {
  const fromSchema = textOf(el.querySelector('[itemprop="name"]'));
  if (fromSchema) return fromSchema;

  for (const heading of el.querySelectorAll('h1, h2, h3, h4, h5, h6')) {
    const title = textOf(heading);
    if (title && title.length <= 120) return title;
  }

  for (const className of ['product-title', 'product-name', 'card-title', 'title']) {
    const titled = textOf(el.querySelector(`.${className}`));
    if (titled) return titled;
  }

  const linkText = textOf(el.querySelector('a[href]'));
  if (linkText && linkText.length <= 80) return linkText;

  return undefined;
}

function resolveProductUrl(el: Element, baseUrl: string): string | undefined {
  const link =
    el.querySelector<HTMLAnchorElement>('a[href][itemprop="url"]') ??
    el.querySelector<HTMLAnchorElement>('a[href]');
  if (!link?.href) return undefined;

  const href = link.getAttribute('href') ?? link.href;
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
    return undefined;
  }

  try {
    return new URL(href, baseUrl).href;
  } catch {
    return link.href;
  }
}

function hasMeaningfulSize(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.height >= MIN_CARD_HEIGHT_PX && rect.width >= MIN_CARD_WIDTH_PX;
}

/** Card-like block without client-specific data attributes. */
function isGenericCardLike(el: Element): boolean {
  if (!hasMeaningfulSize(el)) return false;

  const link = el.querySelector('a[href]');
  const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
  const image = el.querySelector('img, picture, source, svg');

  if (!link && !heading) return false;
  if (!heading && !image) return false;

  const text = el.textContent ?? '';
  if (text.length > 600) return false;

  return true;
}

function findRepeatedSiblingCards(doc: Document): Element[] {
  const containers = doc.querySelectorAll(
    'main, section, article, ul, ol, div, [class*="grid"], [class*="list"], [class*="catalog"]',
  );

  for (const container of containers) {
    const siblings = Array.from(container.children).filter(isGenericCardLike);
    if (siblings.length < MIN_REPEATED_SIBLINGS) continue;

    const heights = siblings.map((el) => el.getBoundingClientRect().height);
    const avgHeight = heights.reduce((sum, h) => sum + h, 0) / heights.length;
    const similarHeight = siblings.filter(
      (el, index) => Math.abs(heights[index]! - avgHeight) < avgHeight * 0.45,
    );

    if (similarHeight.length >= MIN_REPEATED_SIBLINGS) {
      return similarHeight.slice(0, MAX_OBSERVED_NODES);
    }
  }

  return [];
}

/** Drop nested duplicates — keep the outermost card container. */
function dedupeNestedCandidates(elements: Element[]): Element[] {
  const set = new Set(elements);
  return elements.filter((el) => {
    let parent = el.parentElement;
    while (parent) {
      if (set.has(parent)) return false;
      parent = parent.parentElement;
    }
    return true;
  });
}

/** Heuristic product cards for IntersectionObserver (phase 5.7). */
export function findFocusCardCandidates(
  doc: Document = document,
): FocusCardCandidate[] {
  const baseUrl = doc.baseURI || window.location.href;
  const collected: Element[] = [];

  for (const el of doc.querySelectorAll(FOCUS_CARD_SELECTORS)) {
    if (!hasMeaningfulSize(el)) continue;
    collected.push(el);
  }

  if (collected.length < MIN_REPEATED_SIBLINGS) {
    collected.push(...findRepeatedSiblingCards(doc));
  }

  const unique = dedupeNestedCandidates(
    [...new Set(collected)].slice(0, MAX_OBSERVED_NODES),
  );

  return unique.map((element) => ({
    element,
    productName: resolveProductName(element),
    productUrl: resolveProductUrl(element, baseUrl),
  }));
}
